<template>
  <div class="rm-layout" :key="query_version">
    <div class="rm-header">
      <v-progress-circular
        v-if="loading"
        indeterminate
        color="primary"
      ></v-progress-circular>

      <v-btn 
        rounded
        small
        fab
        color="primary" 
        @click="action_new"
      >
        <v-icon small>mdi-plus-thick</v-icon>
      </v-btn>
      <div>

        <ViewTitle title="Matriz de riesgos"/>
      </div>
    </div>

    <div class="rm-layout-dual">
      <div class="rm-list">
        <div
          v-for="rm in matrixes"
          :key="rm.id"
        >
          <v-btn
            width="320px"
            height="52px"
            text
            :color="(rm_id == rm.id) ? 'primary' : ''"
            @click="set_current(rm)"
          >
            {{ rm.matrix_reference }}
          </v-btn>
        </div>

        <div v-if="!matrixes || matrixes.length <= 0">
          <p>No hay matrices definidas</p>
          <p>Si deseas editar la matriz por defecto de RISK_STANDARD. Crea una nueva usando "RISK_STANDARD" como referencia.</p>
        </div>
      </div>

      <div class="rm-current" v-if="rm_id">
        <div class="rm-horizontal">
          <v-btn color="primary" @click="action_edit()">EDITAR</v-btn>
          <v-btn color="secondary" @click="action_delete()">BORRAR</v-btn>
          <h2 style="min-width: 64px">{{ rm_id }}</h2>
          <h2>{{ rm_matrix_reference }}</h2>
        </div>

        <div>
          <p>NOTA: Al editar la matriz se usara para los nuevos riesgos. No obstante los riesgos existentes van a mantener la matriz con la que fueron creados.</p>
        </div>

        <div class="rm-block">
          <h2>Formula</h2>
          <IndicatorFormula
            :metrics="rm_metrics"
            :metrics_formula="rm_metrics_formula"
          />
        </div>

        <div class="rm-block">
          <h2>Matriz</h2>
          <RiskMatrix
            :metrics="rm_metrics"
            :metrics_formula="rm_metrics_formula"
            :interpreter="rm_matrix"
          />
        </div>

        <div class="rm-block">
          <h2>Tabla y métricas</h2>
          <div class="rm-horizontal">
            <RiskTable :interpreter="rm_matrix"/>
            <IndicatorMetrics :metrics="rm_metrics"/>
          </div>
        </div>
      </div>
    </div>

    <ModalDialogForm
      :title="modal_title"
      :showModalDialog="show_modal_dialog"
      :fields_to_collect="fields_to_collect"
      submit_button_text="Save"
      validate
      @close-dialog="hide_dialog"
      @submit="submit_action"
      :loading="loading"
    />

    <ConfirmDialog ref="confirm_dialog" />
  </div>
</template>


<style lang="scss">
 @import "@/sass/variables.scss";

 .rm-horizontal {
   display: flex;
   flex-direction: row;
   gap: 8px;
 }

 .rm-layout {
   display: flex;
   flex-direction: column;
   padding: 18px;
   width: 100%;
   min-height: 90%;
 }

 .rm-header {
   display: flex;
   flex-direction: row;
   padding: 0px;
   gap: 16px;
   align-items: center;
 }

 .rm-layout-dual {
   margin-top: 8px;
   display: flex;
   flex-direction: row;
   padding: 0px;
   gap: 16px;
 }

 .rm-list {
   width: 30%;
   min-width: 340px;
   max-width: 600px;
   display: flex;
   flex-direction: column;
   padding: 0px;
   gap: 0px;
 }

 .rm-current {
   width: 100%;
   display: flex;
   flex-direction: column;
   padding: 0px;
   gap: 16px;
 }

 .rm-block {
   padding-top: 8px;
   padding-bottom: 8px;
 }

 .rm-block > h2 {
   font-size: 18px;
   color: $primary;
 }
</style>


<script>
 import { mapState, mapActions, mapMutations, mapGetters } from 'vuex';
 import QRiskMatrix from '@/api/QRiskMatrix';
 import ModalDialogForm from '@/components/explorer/ModalDialog';
 import IndicatorFormula from '@/components/indicator/IndicatorFormula';
 import IndicatorMetrics from '@/components/indicator/IndicatorMetrics';
 import RiskMatrix from '@/components/indicator/RiskMatrix';
 import RiskTable from '@/components/indicator/RiskTable';
 import ViewTitle from '@/components/ViewTitle';
 import ConfirmDialog from '@/components/ConfirmDialog';

 const NOOP = (() => {});

 const DEFAULT_METRICS_FORMULA = 'mul';

 const DEFAULT_METRICS = [
   {
     "id": 0,
     "name": "Probabilidad",
     "description": "",
     "weight": "1",
     "type": "category",
     "metric_config": {
       "categories": {
         "Remoto": 1,
         "Infrecuente": 4,
         "Ocasional": 7,
         "Frecuente": 10
       }
     }
   },
   {
     "id": 1,
     "name": "Consecuencias",
     "description": "",
     "weight": "1",
     "type": "category",
     "metric_config": {
       "categories": {
         "Evento menor": 1,
         "Evento moderado": 4,
         "Evento importante": 7,
         "Evento catastrófico": 10
       }
     }
   }
 ];

 const DEFAULT_MATRIX = [
   {
     "min": 0,
     "max": 10,
     "label": "Bajo",
     "color": "#5f5",
     "important": false
   },
   {
     "min": 10,
     "max": 39,
     "label": "Moderado",
     "color": "#ff5",
     "important": false
   },
   {
     "min": 39,
     "max": 69,
     "label": "Alto",
     "color": "#fa5",
     "important": true
   },
   {
     "min": 69,
     "max": 100,
     "label": "Extremo",
     "color": "#f55",
     "important": true
   }
 ];

 function copy(data) {
   return JSON.parse(JSON.stringify(data));
 }

 export default {
   name: 'RiskMatrixAdmin',

   components: {
     ModalDialogForm,
     IndicatorFormula,
     IndicatorMetrics,
     RiskMatrix,
     RiskTable,
     ViewTitle,
     ConfirmDialog,
   },

   data: () => ({
     loading: false,
     matrixes: {},
     show_modal_dialog: false,
     submit_action: NOOP,
     rm_id: null,
     rm_matrix_reference: '',
     rm_matrix: {},
     modal_title: 'title',
     query_version: 1,
   }),

   computed: {
     ...mapGetters(['site_id']),

     fields_to_collect() {
       return [
         [
           {
             id: 'id',
             label: 'Id de la matriz',
             required: false,
             value: this.rm_id,
             readonly: true,
             cols: 2,
             class: null,
             component: 'TextInput',
             placeholder: 'Automatic Id',
           },
           {
             id: 'matrix_reference',
             label: 'Referencia',
             required: true,
             value: this.rm_matrix_reference,
             readonly: false,
             cols: 4,
             class: null,
             component: 'TextInput',
             placeholder: "Centro's name",
           },
           {
             id: 'metrics_formula',
             label: 'Formula',
             required: true,
             value: this.rm_metrics_formula,
             readonly: false,
             cols: 2,
             class: null,
             component: 'Select',
             multiple: false,
             placeholder: "Formula",
             items: [
               ["sum", "Acumulación"],
               ["mul", "Multiplicación"],
               // ["avg", "Media"],  // Unsupported in frontend
               // ["max", "Máximo"], // Unsupported in frontend
               // ["min", "Mínimo"], // Unsupported in frontend
             ],
           },
         ], [
           {
             id: 'metrics',
             label: "Métricas",
             required: false,
             value: copy(this.rm_metrics || null),
             readonly: false,
             cols: 6,
             class: null,
             component: 'IndicatorMetricInput',
             placeholder: "Métricas",
             allowed_types: ['category'],
           },
           {
             id: 'matrix',
             label: "Matriz",
             required: false,
             value: copy(this.rm_matrix || null),
             readonly: false,
             cols: 6,
             class: null,
             component: 'IndicatorRiskInterpreter',
             placeholder: "Matriz de riesgo",
           },
         ],
       ];
     },
   },

   async mounted() {
     this.loading = true;
     await this.query_matrixes();
     if (this.matrixes && this.matrixes.length > 0) {
       await this.set_current(this.matrixes[0]);
     } else {
       await this.set_current();
     }
     this.loading = false;
   },

   methods: {
     hide_dialog() {
       this.show_modal_dialog = false;
     },

     async query_matrixes() {
       this.matrixes = await QRiskMatrix.list(this.site_id);
       this.query_version++;
     },

     async set_current(risk_matrix) {
       // Param centro → Current centro object
       // Call with no parameter to reset
       const rm = risk_matrix !== undefined ? risk_matrix : {};
       this.rm_id               = rm.id               || null;
       this.rm_matrix           = rm.matrix           || copy(DEFAULT_MATRIX);
       this.rm_metrics          = rm.metrics          || copy(DEFAULT_METRICS);
       this.rm_metrics_formula  = rm.metrics_formula  || copy(DEFAULT_METRICS_FORMULA);
       this.rm_matrix_reference = rm.matrix_reference || '';
       await this.$nextTick();
     },

     async action_new() {
       await this.set_current();
       this.modal_title = `Nueva matriz de riesgo`;
       this.submit_action = this.save_new;
       this.show_modal_dialog = true;
     },

     async action_edit(rm) {
       if (rm !== undefined) {
         await this.set_current(rm);
       }
       console.log(this, this.rm_matrix_reference);
       this.modal_title = 'Editar matriz ' + this.rm_matrix_reference;
       this.submit_action = this.save_edit;
       this.show_modal_dialog = true;
     },

     async action_delete(rm) {
       if (rm !== undefined) {
         await this.set_current(rm);
       }
       const ok = await this.$refs.confirm_dialog.launch({
         title: `Borrar ${this.rm_matrix_reference}?`,
         text: `¿Realmente desaes borrar permanentemente la matriz para ${this.rm_matrix_reference}, con la id ${this.rm_id}?`,
         yes: 'Borrar permanentemente',
         no: 'Cancelar',
         yes_last: true,
       });
       if (ok) {
         this.loading = true;
         console.log("BORRAR!", this.rm_id);
         await QRiskMatrix.delete(this.site_id, this.rm_id)
         await this.query_matrixes();
         this.set_current();
         this.loading = false;
       }
     },

     async save_new(form_data) {
       try {
         this.loading = true;
         await QRiskMatrix.post(this.site_id, form_data);
         await this.query_matrixes();
         this.show_modal_dialog = false;
       } catch (err) {
         console.error("RiskMatrix.save_new exception ", err);
       }
       this.loading = false;
     },

     async save_edit(form_data) {
       try {
         this.loading = true;
         await QRiskMatrix.put(this.site_id, this.rm_id, form_data);
         await this.query_matrixes();
         this.show_modal_dialog = false;
         this.set_current();
       } catch (err) {
         console.error("RiskMatrix.save_edit exception ", err);
       }
       this.loading = false;
     },
   },
 }
</script>
